import api from '@/api'
import vuex from '@/store'
import catchErrors from './catchErrors'
import { utils } from '@/utils'

const clearCache = (systemName) => {
  const station = vuex.getters.currentStation.station

  return catchErrors.delete(`/v2/stagehand/${station}/systems/${systemName}`)
}

export default {
  clearCache,
  getAllNewsdeskItems(skipCache) {
    const station = vuex.getters.currentStation.station

    const params = {
      'page[limit]': 400,
      hasSmartsheetId: true,
      basic: true
    }
    return catchErrors.get(`/v2/stagehand/${station}/stories`, {
      params,
      cache: {
        maxAge: 3 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getTemplates(skipCache, system) {
    const station = vuex.getters.currentStation.station

    const params = {
      'page[limit]': 400,
      templates: true
    }
    if (system) params.system = system
    return catchErrors.get(`/v2/stagehand/${station}/stories`, {
      params,
      cache: {
        maxAge: 3 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getRundownItems(rundownName, skipCache, system) {
    const station = vuex.getters.currentStation.station

    const params = {
      'page[limit]': 800,
      basic: true
    }
    if (system) params.system = system
    // @warning 200 item limit could mean we miss story data. There are commonly 400+ items per rundown
    return catchErrors.get(`/v2/stagehand/${station}/rundowns/${rundownName}/stories`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  getRundown(rundownName, skipCache, system) {
    const station = vuex.getters.currentStation.station

    const params = {}
    if (system) params.system = system
    return catchErrors.get(`/v2/stagehand/${station}/rundowns/${rundownName}`, {
      params,
      cache: {
        maxAge: 30 * 1000,
        ignoreCache: skipCache ?? false
      }
    })
  },
  reorderRundown(rundownName, reorderItems, system) {
    const station = vuex.getters.currentStation.station

    const params = {}
    if (system) params.system = system
    const payload = {
      data: {
        type: 'rundowns',
        attributes: {
          reorderItems
        }
      }
    }
    return catchErrors.patch(`/v2/stagehand/${station}/rundowns/${rundownName}`, payload, {
      params
    })
  },
  getStory(remote_id, system, station = vuex.getters.currentStation.station) {
    const params = {
      block: true,
      include: 'siblings'
    }
    if (system) params.system = system

    return catchErrors.get(`/v2/stagehand/${station}/stories/${remote_id}`, {
      params,
      cache: {
        maxAge: 0
      }
    })
  },

  async getStoryBulletPoints(remote_id, system, story_id, station = vuex.getters.currentStation.station) {
    const params = {
      getBulletPoints: true,
      ndStoryId: story_id
    }
    if (system) params.system = system
    if (!vuex.getters.isStagehandRunning && station == vuex.getters.currentStation.station) throw new Error('Stagehand is not running')

    let response = await catchErrors.get(`/v2/stagehand/${station}/stories/${remote_id}`, {
      params,
      cache: {
        maxAge: 0
      }
    })
    return response.data.data.attributes.bulletPoints
  },
  async getStoryWebCopy(remote_id, system, story_id, station = vuex.getters.currentStation.station) {
    const params = {
      getWebCopy: true,
      ndStoryId: story_id
    }
    if (system) params.system = system
    if (!vuex.getters.isStagehandRunning && station == vuex.getters.currentStation.station) throw new Error('Stagehand is not running')

    let response = await catchErrors.get(`/v2/stagehand/${station}/stories/${remote_id}`, {
      params,
      cache: {
        maxAge: 0
      }
    })
    return response.data.data.attributes.webCopy
  },
  getStories(ids, system, station = vuex.getters.currentStation.station) {
    const params = {
      'filter[id]': ids.join(',')
    }
    if (system) params.system = system
    if (!vuex.getters.isStagehandRunning && station == vuex.getters.currentStation.station) throw new Error('Stagehand is not running')

    return catchErrors.get(`/v2/stagehand/${station}/stories`, {
      params,
      cache: {
        maxAge: 0
      }
    })
  },
  updateStory(id, { showPath, fields = [], head = [], body, bodyHtml, bodyText, tags, lead, position }, system) {
    // dont allow stories to be updated from outside of the current station
    const station = vuex.getters.currentStation.station

    const fObj = {}
    const hObj = {}
    fields.forEach((f) => {
      fObj[f.name] = f.value
    })

    head.forEach((h) => {
      hObj[h.name] = h.value
    })

    const params = {}
    if (system) params.system = system
    const payload = {
      data: {
        type: 'stories',
        attributes: {
          showPath,
          fields: fObj,
          head: hObj,
          body,
          bodyHtml,
          bodyText,
          tags,
          lead,
          position
        }
      }
    }
    return catchErrors.patch(`/v2/stagehand/${station}/stories/${id}`, payload, { params })
  },
  deleteStory(id, system) {
    // dont allow stories to be deleted from outside of the current station
    const station = vuex.getters.currentStation.station
    const params = {}
    if (system) params.system = system

    return catchErrors.delete(`/v2/stagehand/${station}/stories/${id}`, { params })
  },
  createStory(showPath, { fields = [], body, bodyHtml, bodyText, tags, position }, system) {
    // dont allow stories to be created from outside of the current station
    const station = vuex.getters.currentStation.station

    let fObj = {}
    if (Array.isArray(fields))
      fields.forEach((f) => {
        fObj[f.name] = f.value
      })
    else fObj = fields
    const options = {
      params: {}
    }
    if (system) options.params.system = system
    const payload = {
      data: {
        type: 'stories',
        attributes: {
          showPath,
          position,
          fields: fObj,
          body,
          bodyHtml,
          bodyText,
          tags
        }
      }
    }
    return catchErrors.post(`/v2/stagehand/${station}/stories/`, payload, options)
  },

  async addPlaceholderFromAssignment(assignment) {
    if (!assignment?.platform || !assignment.format) return
    // if a story was not passed get the story info
    if (!assignment.story) {
      const resp = await api.newsdesk.getStoryWithDetails(assignment.story_id)
      if (!resp) throw new Error(`Could not get story ${assignment.story_id}`)
      assignment.story = resp
    }
    // dont add placeholder if story is not approved
    if (!assignment.story || assignment.story.story_approved?.toLowerCase() !== 'approved') return
    // check to see if we should push
    const autoPushStart = utils.stagehandUtils.getAssignmentAutoPushStartTime(assignment)
    const endTime = utils.stagehandUtils.getAssignmentShowEndTime(assignment)
    if (autoPushStart?.isAfter(Date.now()) || endTime?.isBefore(Date.now())) return

    let platform = utils.stagehandUtils.getPlatformByName(assignment.platform)
    if (!platform) throw new Error(`could not find a platform named ${assignment.platform}`)
    let showPath = utils.stagehandUtils.getShowByName(assignment.platform)?.path
    let systemName = platform?.system?.toLowerCase()
    if (!showPath) throw new Error(`could not find a show named ${assignment.platform} in ${systemName}`)
    let reporterEmail
    let videographerEmail
    try {
      reporterEmail = assignment.reporter.email
      videographerEmail = assignment.videographer.email
    } catch (error) {}
    let data = {
      fields: [
        { name: 'p-sheetid', value: assignment.story.id },
        { name: 'p-rowid', value: assignment.id },
        { name: 'title', value: assignment.story.title },
        { name: 'v-format', value: assignment.format },
        { name: 'v-archive', value: true },
        { name: 'reporter', value: reporterEmail },
        { name: 'videographer', value: videographerEmail }
      ],
      bodyHtml: utils.generatePlaceholderHtml(assignment.platform)
    }
    try {
      let resp = await this.createStory(showPath, data, systemName)

      // Since story data not automatically refreshed, update remote and remote simple ids
      return resp?.data?.data
    } catch (err) {
      console.error(`Error pushing to ${systemName}`, err)
      return
    }
  },

  async updatePlaceholderFromAssignment(assignment) {
    if (!assignment?.platform || !assignment.format) return

    let remote_id = assignment.remote_id.toString().split('.')[0]
    let platform = utils.stagehandUtils.getPlatformByName(assignment.platform)
    let systemName = platform?.system?.toLowerCase()
    let showPath = utils.stagehandUtils.getShowByName(assignment.platform)?.path

    try {
      let resp = await this.updateStory(remote_id, { showPath }, systemName)
      return resp?.data?.data
    } catch (err) {
      console.error(`Error pushing to ${systemName}`, err)
      return
    }
  },

  checkApproval(remote_id, system) {
    const station = vuex.getters.currentStation.station

    const params = {
      checkLockStatus: true
    }
    if (system) params.system = system
    return catchErrors.get(`/v2/stagehand/${station}/stories/${remote_id}`, {
      params,
      cache: {
        maxAge: 0
      }
    })
  }
}
